import { useContext, useEffect, useState } from 'react';

import { FilterOptions, Sort } from '@vimeo/comments-ui';
import { PostAlbumVideoCommentsFields } from '@vimeo/gctl-api/fetchers/album/video/comments';
import { PostVideoCommentsFields } from '@vimeo/gctl-api/fetchers/video/comments';
import { usePostAlbumVideoComments } from '@vimeo/gctl-api/hooks/useAlbumVideoComments';
import { usePostVideoComments } from '@vimeo/gctl-api/hooks/useVideoComments';
import { usePostVideoPrivateComments } from '@vimeo/gctl-api/hooks/useVideoPrivateComments';
import { translate } from '@vimeo/i18n-runtime';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import { Position } from '../../AnnotationsOverlay/types';
import { AnalyticsPropsType } from '../../CommentsCommon/types';
import { bpPostComment } from '../../utils/analytics';
import { POST_PUBLIC_COMMENT_FIELDS } from '../../utils/constants';
import {
  checkForMention,
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
  getVideoIdFromClipRequestId,
  idFromUri,
} from '../../utils/helper';
import { guestLoginModalContext } from '../use-guestlogin-modal';
import { useLoginModal } from '../use-login-modal';
import { useVideoData } from '../use-video-data';
import { useAuthenticatedViewer } from '../use-viewer';
import { useViewer } from '../use-viewer';
import { usePrivateComments, usePublicComments } from './use-get-comments';

/**
 * A customized hook to handle posting Public and Private comments data depending on privacy
 */
export function usePostComments(
  clipRequestId: string,
  isPublic: boolean,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const usePostCommentsHook = isPublic ? usePostPublicComment : usePostPrivateComment;
  return usePostCommentsHook(
    clipRequestId,
    sort,
    analyticsProps,
    filterOptions,
    showcaseId,
    reviewId,
  );
}

/**
 * A hook to handle posting Private comments data
 */
export function usePostPrivateComment(
  clipRequestId: string,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const viewer = useViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const [postVideoPrivateComments, { data: newComment, loading, error }] =
    usePostVideoPrivateComments();
  const { revalidateComments } = usePrivateComments(
    clipRequestId,
    sort,
    filterOptions,
    undefined,
    reviewId,
  );
  const [activeUri, setActiveUri] = useState<string | null>(null);
  const { videoData } = useVideoData(clipRequestId, undefined, reviewId);

  const { toggleGuestLoginModal, guestUser } = useContext(guestLoginModalContext);
  const isLoggedIn = useAuthenticatedViewer() || !!guestUser;
  const password = getReviewPasswordHashFromCookie(reviewId);
  const guestUserName = guestUser ? JSON.parse(guestUser)?.guestUserName : null;
  const guestUserId = guestUser ? JSON.parse(guestUser)?.guestUserId : null;

  const isViewerHome = analyticsProps?.productContextFields?.product === 'viewer_home'; // TODO: Clean up after comments 1.0 release
  const post = async (text: string, timeCode?: number, coordinates?: Position) => {
    if (!isLoggedIn) {
      toggleGuestLoginModal(true);
      return;
    }
    await postVideoPrivateComments({
      where: { videoId: clipRequestId as unknown as number },
      select: [
        'metadata.connections.replies.total',
        'text',
        'richtext',
        'uri',
        'metadata.connections.user',
        'createdTime',
        'replies.text',
        'replies.richtext',
        'replies.createdTime',
        'replies.metadata.connections.user',
        'replies.uri',
        'replies.userName',
        'metadata',
        'timeCode',
        'userName',
      ],
      // @ts-expect-error controller annotations are not upto date, text is mandatory.
      variables: {
        richtext: text,
        appOrigin: isViewerHome ? 'viewer_home' : 'svv',
        timeCode,
        coordinates,
        name: guestUserName,
        guestUserId,
        reviewLinkId: reviewId,
      },
      query: { reviewId, password },
    });
  };

  useEffect(() => {
    if (newComment && !loading && !error && activeUri !== newComment.uri) {
      const uploaderId = Number((videoData?.uploader?.link || '').match(/user(\d+)/)?.[1] || 0);
      const videoPrivacy = videoData?.privacy.view;
      setActiveUri(() => newComment.uri);
      if (analyticsProps) {
        const isMention = checkForMention(newComment?.richtext || newComment?.text || '');
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(newComment.uri) as number,
            is_reply: false,
            comment_owner_id: (viewer?.user?.id as number) ?? -1,
            comment_length: newComment.text.length,
            is_mention: isMention,
          },
        };
        bpPostComment(
          videoId,
          videoPrivacy,
          uploaderId,
          analytics,
          viewer,
          !!newComment.timeCode,
          false,
        );
      }
      revalidateComments();
    }
  }, [
    newComment,
    loading,
    error,
    revalidateComments,
    videoId,
    viewer,
    videoData?.privacy.view,
    videoData?.uploader?.link,
    activeUri,
    analyticsProps,
  ]);

  return {
    postComment: post,
    postCommentLoading: loading,
    postCommentError: !!error ? { errorMessage: translate('Unable to post comment') } : null,
    lastAddedComment: idFromUri(newComment?.uri),
  };
}

/**
 * A hook to handle posting Public comments data
 */
export function usePostPublicComment(
  clipRequestId: string,
  sort?: Sort,
  analyticsProps?: AnalyticsPropsType,
  filterOptions?: FilterOptions,
  showcaseId?: number,
) {
  const viewer = useViewer();
  const videoId = getVideoIdFromClipRequestId(clipRequestId);
  const [postPublicComment, videoComments] = usePostVideoComments();
  const [postAlbumPublicComment, albumVideoComments] = usePostAlbumVideoComments();
  const { data: newComment, loading, error } = showcaseId ? albumVideoComments : videoComments;
  const { revalidateComments } = usePublicComments(clipRequestId, sort, filterOptions, showcaseId);
  const [activeUri, setActiveUri] = useState<string | null>(null);
  const { videoData } = useVideoData(clipRequestId, showcaseId);
  const videoPrivacy = videoData?.privacy.view;

  const { toggleGuestLoginModal, guestUser } = useContext(guestLoginModalContext);
  const isLoggedIn = useAuthenticatedViewer() || !!guestUser;
  const { toggleLoginModal, setRedirectUrl } = useLoginModal();

  const password = getPasswordHashFromCookie(clipRequestId);

  const post = async (text: string) => {
    // Check if user is logged out and show login modal
    if (!isLoggedIn && videoPrivacy === 'unlisted') {
      toggleGuestLoginModal(true);
      return;
    } else if (!isLoggedIn) {
      setRedirectUrl(window.location.pathname + window.location.search);
      toggleLoginModal(true);
      return;
    }

    const commentText = { richtext: text };
    // If logged in, post the comment

    if (showcaseId) {
      const showcasePassword = getShowcasePasswordHashFromCookie(showcaseId);
      await postAlbumPublicComment({
        where: {
          albumId: showcaseId,
          videoId: clipRequestId as unknown as number,
        },
        select: POST_PUBLIC_COMMENT_FIELDS as PostAlbumVideoCommentsFields,
        variables: commentText,
        ...(showcasePassword ? { query: { password: showcasePassword } } : {}),
      });
    } else {
      await postPublicComment({
        where: {
          videoId: clipRequestId as unknown as number,
        },
        select: POST_PUBLIC_COMMENT_FIELDS as PostVideoCommentsFields,
        variables: commentText,
        ...(password ? { query: { password } } : {}),
      });
    }
  };

  useEffect(() => {
    if (newComment && !loading && !error && activeUri !== newComment.uri) {
      const uploaderId = Number((videoData?.uploader?.link || '').match(/user(\d+)/)?.[1] || 0);
      setActiveUri(() => newComment.uri);
      if (analyticsProps) {
        const isMention = checkForMention(newComment?.text || '');
        let analytics = {
          ...analyticsProps,
          commentContextFields: {
            comment_id: idFromUri(newComment.uri) as number,
            is_reply: false,
            comment_owner_id: (viewer?.user?.id as number) ?? -1,
            comment_length: newComment.text.length,
            is_mention: isMention,
          },
        };
        bpPostComment(videoId, videoPrivacy, uploaderId, analytics, viewer);
      }
      revalidateComments();
    }
  }, [
    newComment,
    loading,
    error,
    revalidateComments,
    videoId,
    viewer,
    videoData?.privacy.view,
    videoData?.uploader?.link,
    activeUri,
    analyticsProps,
    videoPrivacy,
  ]);

  return {
    postComment: post,
    postCommentLoading: loading,
    postCommentError: !!error ? { errorMessage: translate('Unable to post comment') } : null,
    lastAddedComment: idFromUri(newComment?.uri),
  };
}
